import type { IAuthUser } from '@kfolks/types'
import { apiClient } from '@/shared/api/api.client'

const ENTRA_ID_TENANT_ID = import.meta.env.VITE_ENTRA_ID_TENANT_ID
const ENTRA_ID_CLIENT_ID = import.meta.env.VITE_ENTRA_ID_CLIENT_ID
const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI || `${window.location.origin}/auth/callback`

export const authService = {
	initiateEntraLogin: () => {
		const params = new URLSearchParams({
			client_id: ENTRA_ID_CLIENT_ID,
			response_type: 'code',
			redirect_uri: REDIRECT_URI,
			scope: 'openid profile email',
			response_mode: 'query',
		})
		const authUrl = `https://login.microsoftonline.com/${ENTRA_ID_TENANT_ID}/oauth2/v2.0/authorize?${params}`
		window.location.href = authUrl
	},

	/**
	 * Exchange the Entra authorization code. The backend sets the HttpOnly
	 * `access_token` cookie via `Set-Cookie` and responds 204 — there is no
	 * JWT in the response body. After a successful exchange we fetch the
	 * viewer profile via `/auth/me` (over the freshly-set cookie) so the
	 * caller can drive role-based post-login routing.
	 */
	handleCallback: async (code: string): Promise<IAuthUser> => {
		await apiClient.post<void>('/auth/callback', { code })
		const me = await apiClient.get<IAuthUser>('/auth/me')
		return me.data
	},

	/**
	 * Fetches the authenticated viewer. The browser attaches the
	 * `access_token` cookie automatically; no token argument is needed.
	 */
	getCurrentUser: async (): Promise<IAuthUser> => {
		const response = await apiClient.get<IAuthUser>('/auth/me')
		return response.data
	},

	/**
	 * Clear the server-side session cookie. Idempotent (204 regardless of
	 * whether a session existed). Errors are swallowed — even if the call
	 * fails, the user-visible Entra logout still proceeds.
	 */
	clearServerSession: async (): Promise<void> => {
		try {
			await apiClient.post('/auth/logout')
		} catch {
			// Best-effort. The Entra redirect below still ends the session for
			// the user; a lingering cookie expires on its own Max-Age.
		}
	},

	/**
	 * Redirects to Entra ID's logout endpoint. Combine with
	 * `clearServerSession()` for a full sign-out.
	 */
	logout: () => {
		const logoutUrl = `https://login.microsoftonline.com/${ENTRA_ID_TENANT_ID}/oauth2/v2.0/logout?post_logout_redirect_uri=${window.location.origin}`
		window.location.href = logoutUrl
	},
}
